type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

const KEY_PREFIX = "field_"
const MAX_LENGTH = 254
const IDLE_MS = 1500

const FIELD_TAGS = [ "INPUT", "SELECT", "TEXTAREA" ]

const SKIPPED_TYPES = [
  "button", "checkbox", "color", "file", "hidden",
  "image", "password", "radio", "range", "reset", "submit"
]

const CREDENTIAL = /pass|secret|token|otp|ssn|cvc|cvv/i

const sent = new Map<string, string>()

let pending: Field | null = null
let idle: ReturnType<typeof setTimeout> | undefined
let watching = false

function isField(node: EventTarget | null): node is Field {
  return node instanceof Element && FIELD_TAGS.includes(node.tagName)
}

function collectable(el: Field): boolean {
  if (SKIPPED_TYPES.includes((el as HTMLInputElement).type)) return false
  if (el.closest("[data-clarity-mask]")) return false

  return !CREDENTIAL.test(`${el.name} ${el.id} ${el.autocomplete ?? ""}`)
}

function keyFor(el: Field): string {
  const slug = (el.name || el.id).toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")

  return slug ? (KEY_PREFIX + slug).slice(0, MAX_LENGTH) : ""
}

function valueFor(el: Field): string {
  const text = el instanceof HTMLSelectElement
    ? Array.from(el.options).filter((option) => option.selected).map((option) => option.text).join(", ")
    : el.value

  return text.trim().slice(0, MAX_LENGTH)
}

function record(el: Field): void {
  if (!collectable(el)) return

  const key = keyFor(el)
  const value = valueFor(el)
  if (!key || !value || sent.get(key) === value) return

  sent.set(key, value)
  window.clarity?.("set", key, value)
}

function flush(): void {
  clearTimeout(idle)
  const el = pending
  pending = null
  if (el) record(el)
}

function schedule(el: Field): void {
  if (pending && pending !== el) flush()

  pending = el
  clearTimeout(idle)
  idle = setTimeout(flush, IDLE_MS)
}

export function watchFields(): void {
  if (watching) return

  watching = true
  document.addEventListener("input", (event) => {
    const el = event.target
    if (isField(el)) schedule(el)
  }, true)
  document.addEventListener("submit", (event) => {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return

    flush()
    for (const el of Array.from(form.elements)) if (isField(el)) record(el)
  }, true)
  document.addEventListener("turbo:before-visit", flush)
  document.addEventListener("turbo:load", () => {
    clearTimeout(idle)
    pending = null
    sent.clear()
  })
}
